import React from 'react'
import { Card, Table, Button, Row, Col } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// 常數定義
const QUALITY_TYPES = {
  0: '很好',
  1: '普通',
  2: '不好',
  3: '非常差',
}

const FEELING_TYPES = {
  0: '精神飽滿',
  1: '心情愉快',
  2: '頭昏腦脹',
  3: '想賴床',
  4: '身體酸痛疲勞',
}

const SPEED_TYPES = {
  0: '很快就睡著',
  1: '躺一陣子才睡著',
  2: '輾轉反側難入睡',
}

const SleepRecord = ({
  data,
  startDate,
  endDate,
  setShowAddForm,
  onAddRecord,
}) => {
  // 獲取本地日期字符串，用於日期比較
  const getLocalDateString = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(date.getDate()).padStart(2, '0')}`
  }
  const validData = Array.isArray(data) ? data : []

  // 處理圖表數據
  const processChartData = () => {
    if (!startDate || !endDate || !Array.isArray(data)) {
      return {
        labels: [],
        datasets: [
          {
            label: '睡眠時段',
            data: [],
          },
        ],
      }
    }

    // 生成日期範圍
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(getLocalDateString(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }
    const allDates = Array.from(dateSet).sort()

    // 過濾並處理有效記錄
    const validRecords = data.filter(
      (record) =>
        record &&
        record.start_time &&
        record.end_time &&
        !isNaN(new Date(record.start_time).getTime()) &&
        !isNaN(new Date(record.end_time).getTime())
    )

    // 計算每條記錄的時間範圍
    const records = validRecords.map((record) => {
      const startDate = new Date(record.start_time)
      const endDate = new Date(record.end_time)

      // 使用結束時間的日期作為記錄日期
      const recordDate = getLocalDateString(endDate)

      // 統一到結束時間的日期
      const normalizedStartDate = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
        startDate.getHours(),
        startDate.getMinutes()
      )

      // 如果是跨天記錄，調整開始時間為0點
      if (getLocalDateString(startDate) !== recordDate) {
        normalizedStartDate.setHours(0, 0, 0)
      }

      // 轉換為小時表示
      const startHours =
        normalizedStartDate.getHours() + normalizedStartDate.getMinutes() / 60
      const endHours = endDate.getHours() + endDate.getMinutes() / 60

      // 計算睡眠時長（分鐘）
      const duration = Math.round((endDate - startDate) / (1000 * 60))

      return {
        date: recordDate,
        timeRange: [startHours, endHours],
        duration,
        quality: record.quality,
        feeling: record.feeling,
        speed: record.speed,
        isAcrossDays: getLocalDateString(startDate) !== recordDate,
      }
    })

    // 格式化日期標籤
    const formatDateTime = (dateStr) => {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    return {
      labels: allDates.map(formatDateTime),
      datasets: [
        {
          label: '睡眠時段',
          data: allDates
            .map((date) => {
              const dayRecords = records.filter((r) => r.date === date)
              if (!dayRecords.length) return null
              return dayRecords.map((record) => ({
                x: formatDateTime(date),
                y: record.timeRange,
                duration: record.duration,
                quality: record.quality,
                feeling: record.feeling,
                speed: record.speed,
              }))
            })
            .flat()
            .filter(Boolean),
          backgroundColor: (context) => {
            const quality = context.raw?.quality
            // 根據睡眠品質設置不同顏色
            switch (quality) {
              case 0:
                return 'rgba(75, 192, 192, 0.7)' // 很好
              case 1:
                return 'rgba(255, 206, 86, 0.7)' // 普通
              case 2:
                return 'rgba(255, 159, 64, 0.7)' // 不好
              case 3:
                return 'rgba(255, 99, 132, 0.7)' // 非常差
              default:
                return 'rgba(201, 203, 207, 0.7)'
            }
          },
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const data = context.raw
            const lines = [
              `時長：${data.duration} 分鐘`,
              `睡眠品質：${QUALITY_TYPES[data.quality]}`,
              `起床感受：${data.feeling.map((f) => FEELING_TYPES[f]).join('、')}`,
              `入睡速度：${SPEED_TYPES[data.speed]}`,
            ]
            return lines
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 24,
        ticks: {
          stepSize: 3,
          callback: function (value) {
            return `${String(value).padStart(2, '0')}:00`
          },
        },
        grid: {
          color: '#CCCCCC',
          borderDash: [2, 2],
        },
        title: {
          display: true,
          text: '時間',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  }

  // 為表格顯示分組數據
  const groupDataByDate = (records) => {
    if (!startDate || !endDate || !Array.isArray(records)) return []

    // 生成日期範圍
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(getLocalDateString(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // 轉換為數組並排序（倒序，最新的在前）
    let allDates = Array.from(dateSet).sort((a, b) => new Date(b) - new Date(a))

    // 創建日期到記錄的映射，使用結束時間的日期
    const recordsByDate = (records || []).reduce((acc, record) => {
      if (!record?.end_time) return acc
      const endDate = new Date(record.end_time)
      const date = getLocalDateString(endDate)
      if (!acc[date]) acc[date] = []
      acc[date].push(record)
      return acc
    }, {})

    // 為每一天創建數據結構
    return allDates.map((date) => ({
      date,
      records: (recordsByDate[date] || []).sort((a, b) => {
        try {
          return new Date(b.end_time) - new Date(a.end_time)
        } catch {
          return 0
        }
      }),
    }))
  }

  // 格式化時間顯示
  const formatTimeRange = (startTime, endTime) => {
    const start = new Date(startTime)
    const end = new Date(endTime)

    // 格式化時間
    const formatTime = (date) => {
      return `${String(date.getHours()).padStart(2, '0')}:${String(
        date.getMinutes()
      ).padStart(2, '0')}`
    }

    // 檢查是否跨天
    const isCrossDay = getLocalDateString(start) !== getLocalDateString(end)

    if (isCrossDay) {
      return `(前日) ${formatTime(start)} ~ ${formatTime(end)}`
    } else {
      return `${formatTime(start)} ~ ${formatTime(end)}`
    }
  }

  // 計算睡眠時長
  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime)
    const end = new Date(endTime)
    // Get the end date string to check for cross-day records
    const startDateStr = start.toISOString().split('T')[0]
    const endDateStr = end.toISOString().split('T')[0]

    // If end date is different from start date, add a day to end time for calculation
    if (endDateStr !== startDateStr) {
      end.setDate(end.getDate() + 1)
    }

    // Calculate duration in minutes and ensure it's positive
    const durationInMinutes = Math.abs(Math.round((end - start) / (1000 * 60)))
    return durationInMinutes
  }

  // 格式化日期
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return `${dateStr} ${weekdays[date.getDay()]}`
  }

  const groupedData = groupDataByDate(data)

  return (
    <Row className="sleep-record g-3">
      {/* Chart Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <Card.Title className="d-flex justify-content-end align-items-center mb-4">
              <div className="ms-3 d-flex gap-3">
                <span>
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(75, 192, 192, 0.7)' }}
                  ></i>
                  很好
                </span>
                <span>
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(255, 206, 86, 0.7)' }}
                  ></i>
                  普通
                </span>
                <span>
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(255, 159, 64, 0.7)' }}
                  ></i>
                  不好
                </span>
                <span>
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(255, 99, 132, 0.7)' }}
                  ></i>
                  非常差
                </span>
              </div>
            </Card.Title>
            <div className="responsive-chart">
              <Bar options={chartOptions} data={processChartData()} />
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Table Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="table-responsive">
              {Array.isArray(groupedData) &&
                groupedData.map((dayGroup) => (
                  <Table
                    key={dayGroup.date}
                    hover
                    className="mb-0 border rounded-3 overflow-hidden"
                  >
                    <thead>
                      <tr className="table-light">
                        <th colSpan="5">{formatDate(dayGroup.date)}</th>
                        <th className="text-end">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="px-3"
                            onClick={() => {
                              setShowAddForm(true)
                              onAddRecord?.(dayGroup.date)
                            }}
                          >
                            <i className="fa-solid fa-plus me-2"></i>
                            新增
                          </Button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayGroup.records.length > 0 ? (
                        dayGroup.records.map((record) => (
                          <tr key={record.action_id}>
                            <td>
                              {formatTimeRange(
                                record.start_time,
                                record.end_time
                              )}
                            </td>
                            <td>
                              {calculateDuration(
                                record.start_time,
                                record.end_time
                              )}{' '}
                              分鐘
                            </td>
                            <td>{QUALITY_TYPES[record.quality]}</td>
                            <td>
                              {record.feeling &&
                                record.feeling
                                  .map((f) => FEELING_TYPES[f])
                                  .join('、')}
                            </td>
                            <td>{SPEED_TYPES[record.speed]}</td>
                            <td>{record.comment || '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            尚無睡眠紀錄
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default SleepRecord
